import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import {
    IconButton, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { Popup } from 'maplibre-gl';
import {
    Fragment, useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import usePositionAttributes from '../common/attributes/usePositionAttributes';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import PositionValue from '../common/components/PositionValue';
import SelectField from '../common/components/SelectField';
import TableShimmer from '../common/components/TableShimmer';
import {
    createPopUp,
    createPopUpReport,
} from '../common/util/mapPopup';
import { useRestriction } from '../common/util/permissions';
import usePersistedState from '../common/util/usePersistedState';
import MapView from '../map/core/MapView';
import MapCamera from '../map/MapCamera';
import MapGeofence from '../map/MapGeofence';
import MapPositions from '../map/MapPositions';
import MapRoutePath from '../map/MapRoutePath';
import MapRoutePoints from '../map/MapRoutePoints';
import MapScale from '../map/MapScale';
import { useCatch } from '../reactHelper';
import scheduleReport from './common/scheduleReport';
import useReportStyles from './common/useReportStyles';
import ColumnSelect from './components/ColumnSelect';
import ReportFilter, { updateReportParams } from './components/ReportFilter';
import ReportsMenu from './components/ReportsMenu';


const PositionsReportPage = () => {
    const navigate = useNavigate();
    const classes = useReportStyles();
    const t = useTranslation();

    const [searchParams, setSearchParams] = useSearchParams();

    const positionAttributes = usePositionAttributes(t);

    const readonly = useRestriction('readonly');

    const [available, setAvailable] = useState([]);
    // const [columns, setColumns] = useState(['fixTime', 'latitude', 'longitude', 'speed', 'address']);
    const [columns, setColumns] = usePersistedState('routeColumns', ['fixTime', 'latitude', 'longitude', 'speed', 'address']);
    console.log(columns);

    const [items, setItems] = useState([]);
    const geofenceId = searchParams.has('geofenceId') ? parseInt(searchParams.get('geofenceId')) : null;
    const [loading, setLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const selectedIcon = useRef();

    useEffect(() => {
        if (selectedIcon.current) {
            selectedIcon.current.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }, [selectedIcon.current]);

    const onMapPointClick = useCallback((positionId) => {
        Array.from(document.getElementsByClassName('maplibregl-popup')).map((item) => item.remove());
        setSelectedItem(items.find((it) => it.id === positionId));
        new Popup()
            .setMaxWidth('400px')
            .setOffset(30)
            .setHTML(createPopUp(items.find((it) => it.id === positionId)))
            .setLngLat([items.find((it) => it.id === positionId).longitude, items.find((it) => it.id === positionId).latitude])
            .addTo(map);
        window.position = items.find((it) => it.id === positionId);
        if (virtuosoRef.current) {
            virtuosoRef.current.scrollToIndex({
                index: items.findIndex((item) => item.id === positionId),
                align: 'start', // or 'center' or 'end'
                behavior: 'smooth', // or 'auto'
            });
        }
    }, [items, setSelectedItem]);

    const handleSubmit = useCatch(async ({ deviceIds, from, to, type }) => {
        const query = new URLSearchParams({ from, to });
        if (geofenceId) {
            query.append('geofenceId', geofenceId)
        }
        deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
        if (type === 'export') {
            await onExport({ deviceIds, from, to });
        } else {
            setLoading(true);
            try {
                const response = await fetch(`/api/positions?${query.toString()}`, {
                    headers: { Accept: 'application/json' },
                });
                const data = await response.json();
                const keySet = new Set();
                const keyList = [];
                data.forEach((position) => {
                    Object.keys(position).forEach((it) => keySet.add(it));
                    Object.keys(position.attributes).forEach((it) => keySet.add(it));
                });
                ['id', 'deviceId', 'outdated', 'network', 'attributes'].forEach((key) => keySet.delete(key));
                Object.keys(positionAttributes).forEach((key) => {
                    if (keySet.has(key)) {
                        keyList.push(key);
                        keySet.delete(key);
                    }
                });
                setAvailable([...keyList, ...keySet].map((key) => [key, positionAttributes[key]?.name || key]));
                setItems(data);
            } finally {
                setLoading(false);
            }
        }
    });

    const onExport = useCatch(async ({ deviceIds, from, to }) => {
        const query = new URLSearchParams({ from, to });
        if (geofenceId) {
            query.append('geofenceId', geofenceId)
        }
        deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
        window.location.assign(`/api/positions/csv?${query.toString()}`);
    });

    const onSchedule = useCatch(async (deviceIds, groupIds, report) => {
        report.type = 'route';
        await scheduleReport(deviceIds, groupIds, report);
        navigate('/reports/scheduled');
    });

    const showPU = (positionId) => {
        console.log('showPU');

        Array.from(document.getElementsByClassName('maplibregl-popup')).map((item) => item.remove());
        setSelectedItem(items.find((it) => it.id === positionId));
        new Popup()
            .setMaxWidth('400px')
            .setOffset(30)
            .setHTML(createPopUpReport(items.find((it) => it.id === positionId)))
            .setLngLat([items.find((it) => it.id === positionId).longitude, items.find((it) => it.id === positionId).latitude])
            .addTo(map);
        window.position = items.find((it) => it.id === positionId);
    };

    return (
        <PageLayout menu={<ReportsMenu />} breadcrumbs={['reportTitle', 'reportPositions']}>
            <div className={classes.container}>
                {selectedItem && (
                    <div className={classes.containerMap}>
                        <MapView>
                            <MapGeofence />
                            {[...new Set(items.map((it) => it.deviceId))].map((deviceId) => {
                                const positions = items.filter((position) => position.deviceId === deviceId);
                                return (
                                    <Fragment key={deviceId}>
                                        <MapRoutePath positions={positions} />
                                        <MapRoutePoints positions={positions} onClick={onMapPointClick} />
                                    </Fragment>
                                );
                            })}
                            <MapPositions positions={[selectedItem]} titleField="fixTime" />
                        </MapView>
                        <MapScale />
                        <MapCamera positions={items} />
                    </div>
                )}
                <div className={classes.containerMain}>
                    <div className={classes.header}>
                        <ReportFilter handleSubmit={handleSubmit} onExport={onExport} handleSchedule={onSchedule} deviceType="single" loading={loading}>
                            <div className={classes.filterItem}>
                                <SelectField
                                    value={geofenceId}
                                    onChange={(e) => {
                                        const values = e.target.value ? [e.target.value] : [];
                                        updateReportParams(searchParams, setSearchParams, 'geofenceId', values);
                                    }}
                                    endpoint="/api/geofences"
                                    label={t('sharedGeofence')}
                                    fullWidth
                                />
                            </div>
                            <ColumnSelect
                                columns={columns}
                                setColumns={setColumns}
                                columnsArray={available}
                                rawValues
                                disabled={!items.length}
                            />
                        </ReportFilter>
                    </div>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.columnAction} />
                                {columns.map((key) => (<TableCell key={key}>{positionAttributes[key]?.name || key}</TableCell>))}
                                <TableCell className={classes.columnAction} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading ? items.slice(0, 4000).map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell className={classes.columnAction} padding="none">
                                        {selectedItem === item ? (
                                            <IconButton size="small" onClick={() => setSelectedItem(null)} ref={selectedIcon}>
                                                <GpsFixedIcon fontSize="small" />
                                            </IconButton>
                                        ) : (
                                            <IconButton size="small" onClick={() => showPU(item.id)}>
                                                <LocationSearchingIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </TableCell>
                                    {columns.map((key) => (
                                        <TableCell key={key}>
                                            <PositionValue
                                                position={item}
                                                property={item.hasOwnProperty(key) ? key : null}
                                                attribute={item.hasOwnProperty(key) ? null : key}
                                            />
                                        </TableCell>
                                    ))}
                                    {/* <TableCell className={classes.actionCellPadding}>
                                        <CollectionActions
                                            itemId={item.id}
                                            endpoint="positions"
                                            readonly={readonly}
                                            setTimestamp={() => {
                                                setItems(items.filter((position) => position.id !== item.id));
                                            }}
                                        />
                                    </TableCell> */}
                                </TableRow>
                            )) : (<TableShimmer columns={columns.length + 1} startAction />)}
                        </TableBody>
                    </Table>
                </div>
            </div>
        </PageLayout>
    );
};

export default PositionsReportPage;